import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Phone } from "lucide-react";
import { api, ENQUIRY_STATUS, fmtDateTime, gbp, telHref, waHref } from "@/admin/api";
import { Badge, Btn, Card, Empty, ErrorMsg, Field, inputCls, Loading, Modal, useAsync } from "@/admin/ui";

const FILTERS = ["all", "new", "contacted", "quoted", "won", "lost"];
const defaultReply = (e) =>
  `Hi ${e.name.split(" ")[0]}, it's Dave from DMS Home Improvements. Thanks for your enquiry about ${e.service.toLowerCase()} in ${e.postcode}. When would suit you for a quick call or a free site visit?`;

const StatusModal = ({ enquiry, status, onClose, onSaved }) => {
  const [value, setValue] = useState("");
  const [error, setError] = useState(null);
  const needsValue = status === "won";
  const askQuote = status === "quoted";
  const save = async (e) => {
    e.preventDefault();
    setError(null);
    const body = { status };
    if (needsValue) {
      if (!value) return setError("Enter the job value in pounds");
      body.job_value = Number(value);
    }
    if (askQuote && value) body.quote_value = Number(value);
    try {
      onSaved(await api(`/admin/enquiries/${enquiry.id}`, { method: "PATCH", body }));
    } catch (err) {
      setError(err.message);
    }
  };
  return (
    <Modal title={needsValue ? "Job won — what's it worth?" : "Quote sent"} onClose={onClose} testId="status-modal">
      <form onSubmit={save} className="space-y-4">
        <Field label={needsValue ? "Job value (£)" : "Quote value (£, optional)"}>
          <input data-testid="value-input" type="number" min="0" step="1" inputMode="numeric" autoFocus value={value} onChange={(e) => setValue(e.target.value)} className={inputCls} placeholder="e.g. 12500" />
        </Field>
        <ErrorMsg error={error} />
        <Btn type="submit" data-testid="value-save" className="w-full">{needsValue ? "Mark as Won" : "Mark as Quoted"}</Btn>
      </form>
    </Modal>
  );
};

const WhatsAppModal = ({ enquiry, onClose }) => {
  const [text, setText] = useState(defaultReply(enquiry));
  return (
    <Modal title="WhatsApp reply" onClose={onClose} testId="whatsapp-modal">
      <p className="mb-3 text-sm text-body">To {enquiry.name} · {enquiry.phone}. Edit the message, then open WhatsApp.</p>
      <textarea data-testid="whatsapp-text" rows={5} value={text} onChange={(e) => setText(e.target.value)} className={`${inputCls} py-3`} />
      <Btn as="a" variant="whatsapp" href={waHref(enquiry.phone, text)} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-open" className="mt-4 w-full">
        <MessageCircle size={20} /> Open WhatsApp
      </Btn>
    </Modal>
  );
};

const EnquiryCard = ({ e, onChange }) => {
  const [modal, setModal] = useState(null); // {type:'status', status} | {type:'wa'}
  const [error, setError] = useState(null);
  const setStatus = async (status) => {
    if (status === "won" || status === "quoted") return setModal({ type: "status", status });
    try {
      onChange(await api(`/admin/enquiries/${e.id}`, { method: "PATCH", body: { status } }));
    } catch (err) {
      setError(err.message);
    }
  };
  return (
    <Card data-testid={`enquiry-${e.id}`} className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-lg font-bold text-navy">{e.name}</p>
          <p className="text-sm text-body">{e.service} · {e.postcode}</p>
          <p className="mt-1 text-xs text-body">{fmtDateTime(e.created_at)}</p>
        </div>
        <Badge map={ENQUIRY_STATUS} value={e.status || "new"} testId={`enquiry-status-${e.id}`} />
      </div>
      {e.message && <p className="whitespace-pre-line rounded-xl bg-alt px-4 py-3 text-sm text-navy">{e.message}</p>}
      <p className="text-sm text-body">
        {e.phone}
        {e.quote_value ? ` · Quoted ${gbp(e.quote_value)}` : ""}
        {e.job_id && (<> · <Link to={`/admin/jobs/${e.job_id}`} data-testid={`enquiry-job-link-${e.id}`} className="font-semibold text-brand">Open job</Link></>)}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <Btn as="a" variant="outline" href={telHref(e.phone)} data-testid={`call-${e.id}`}><Phone size={18} /> Call</Btn>
        <Btn variant="whatsapp" onClick={() => setModal({ type: "wa" })} data-testid={`whatsapp-${e.id}`}><MessageCircle size={18} /> WhatsApp</Btn>
      </div>
      <Field label="Status">
        <select data-testid={`status-select-${e.id}`} value={e.status || "new"} onChange={(ev) => setStatus(ev.target.value)} className={inputCls}>
          {Object.entries(ENQUIRY_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </Field>
      <ErrorMsg error={error} />
      {modal?.type === "status" && <StatusModal enquiry={e} status={modal.status} onClose={() => setModal(null)} onSaved={(u) => { setModal(null); onChange(u); }} />}
      {modal?.type === "wa" && <WhatsAppModal enquiry={e} onClose={() => setModal(null)} />}
    </Card>
  );
};

export default function Enquiries() {
  const { data, error, loading, reload } = useAsync(() => api("/admin/enquiries"), []);
  const [filter, setFilter] = useState("all");
  const [list, setList] = useState(null);
  const items = (list || data || []).filter((e) => filter === "all" || (e.status || "new") === filter);
  const onChange = (updated) => setList((prev) => (prev || data).map((x) => (x.id === updated.id ? updated : x)));
  if (loading) return <Loading />;
  if (error) return <ErrorMsg error={error} />;
  return (
    <div data-testid="enquiries-page" className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-navy md:text-3xl">Enquiries</h1>
        <Btn variant="ghost" onClick={() => { setList(null); reload(); }} data-testid="enquiries-refresh" className="min-h-11 px-3 text-sm">Refresh</Btn>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {FILTERS.map((f) => (
          <button key={f} type="button" data-testid={`filter-${f}`} aria-pressed={filter === f} onClick={() => setFilter(f)}
            className={`min-h-11 shrink-0 rounded-full border px-4 text-sm font-semibold ${filter === f ? "border-brand bg-brand text-white" : "border-line bg-white text-navy"}`}>
            {f === "all" ? "All" : ENQUIRY_STATUS[f].label}
          </button>
        ))}
      </div>
      {items.length === 0 ? (
        <Empty testId="enquiries-empty">No enquiries{filter !== "all" ? ` marked ${ENQUIRY_STATUS[filter].label}` : " yet"}. New quote requests from the website will appear here.</Empty>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">{items.map((e) => <EnquiryCard key={e.id} e={e} onChange={onChange} />)}</div>
      )}
    </div>
  );
}
